import { motion } from 'framer-motion';
import {
  Brain, Network, Cpu, Shield, Activity, Database,
  AlertTriangle, FileText, Landmark, Users, BarChart3,
  ExternalLink, ArrowRight, Globe, Scale, Calendar,
  Banknote, TrendingUp, Leaf, Building2
} from 'lucide-react';

const RISKMIND_URL = 'https://www.riskmind.qa';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const PIPELINE_STAGES = [
  {
    step: '01',
    icon: FileText,
    title: 'Reality Seed',
    desc: 'You supply a structured brief — strategy, goals, roles, internal documents, and external events. RiskMind builds a world model around it.'
  },
  {
    step: '02',
    icon: Network,
    title: 'AM Risk Universe',
    desc: 'A 30–50 node causal graph of every actor, risk, and external force shaping your strategy. 25–60 causal edges. Fully interactive.'
  },
  {
    step: '03',
    icon: Brain,
    title: 'Hive Mind',
    desc: '5–9 parallel AI agents — strategic, operational, financial, cyber, geopolitical, regulatory, environmental — mine risks simultaneously.'
  },
  {
    step: '04',
    icon: Activity,
    title: 'ML Scoring Pipeline',
    desc: 'Random Forest (R²=0.77) calibrates probability. TextCNN (RMSE=0.09) forecasts 30/90-day impact. TF-IDF applies Gulf/MENA domain priors.'
  },
  {
    step: '05',
    icon: BarChart3,
    title: 'Quantitative Engines',
    desc: 'GNN risk matrix + Bow-Tie causal chains + Monte Carlo (VaR/CVaR). No black-box ML — every score is traceable and reproducible.'
  }
];

const MODULES = [
  { icon: Banknote, name: 'Cost Risk', domain: 'Budget, procurement, funding, inflation exposure' },
  { icon: FileText, name: 'Contract Risk', domain: 'Contract terms, delivery obligations, disputes' },
  { icon: Scale, name: 'Legal Risk', domain: 'Regulatory, litigation, compliance, liability' },
  { icon: Calendar, name: 'Schedule Risk', domain: 'Programme delivery, milestones, critical path' },
  { icon: Users, name: 'Training & Competency', domain: 'Skills gaps, capacity, change readiness' },
  { icon: Landmark, name: 'Political Risk', domain: 'Policy, governance, elections, geopolitics' },
  { icon: TrendingUp, name: 'Economic Risk', domain: 'Fiscal, inflation, FX, market conditions' },
  { icon: Globe, name: 'Social Risk', domain: 'Demographic, public trust, community impact' },
  { icon: Cpu, name: 'Technological Risk', domain: 'Cyber, AI, infrastructure, digital dependencies' },
  { icon: Leaf, name: 'Environmental Risk', domain: 'Climate, regulatory, ESG, physical environment' },
  { icon: Building2, name: 'Management View', domain: 'Executive aggregation across all modules' }
];

const PRINCIPLES = [
  {
    icon: Database,
    title: 'Reproducible',
    desc: 'Fixed-seed Monte Carlo. Every risk score is traceable and auditable, end-to-end.'
  },
  {
    icon: Network,
    title: 'Explainable',
    desc: 'Bow-Tie causal chains show exactly why a risk scores the way it does. No black box.'
  },
  {
    icon: Shield,
    title: 'Sovereign',
    desc: 'Quantitative engines run locally. No client data sent to third parties for computation.'
  },
  {
    icon: AlertTriangle,
    title: 'Certifiable',
    desc: 'Architected for ISO 27001, ISO 42001, and SOC 2 Type II from day one.'
  }
];

export default function RiskMindPage() {
  return (
    <div data-testid="riskmind-page">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Flagship Product · Government Risk Intelligence
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            Risk
            <br />
            <span className="text-gradient-cyan">Mind</span>
          </motion.h1>
          <motion.p
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="text-base md:text-lg text-slate-400 font-light max-w-2xl mb-8"
          >
            Government risk intelligence. In minutes. At ministerial standard. RiskMind replaces Excel risk registers and PowerPoint decks with an AI-powered intelligence engine built on our Hive Mind multi-agent architecture.
          </motion.p>
          <motion.div
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href={RISKMIND_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="riskmind-visit"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200 justify-center"
            >
              Visit riskmind.qa <ExternalLink size={16} />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-white/5 transition-colors duration-200 justify-center"
            >
              Request a Demo <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <motion.div {...fadeUp} className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-4">The Problem</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight leading-tight">
                Governments are flying blind on risk.
              </h2>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-7 space-y-6 text-slate-400 text-sm md:text-base leading-relaxed"
            >
              <p>
                Every day, ministers sign off on billion-dollar programmes, policy decisions, and infrastructure investments — using risk processes designed in the 1990s. Risk registers in Excel. Analyses in PowerPoint. Quarterly updates manually assembled and already wrong by the time they land.
              </p>
              <p>
                When the Red Sea shipping crisis hit, how many government risk registers updated overnight? <span className="text-white font-semibold">Zero.</span> When new AI regulation dropped in Brussels, how many infrastructure programmes recalculated their technology exposure automatically? <span className="text-white font-semibold">None.</span>
              </p>
              <p>
                The gap between the speed of the world and the speed of government risk management is widening. <span className="text-white font-semibold">RiskMind closes it.</span>
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pipeline */}
      <section className="py-16 md:py-28 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">How it Works</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight mb-6">
              An <span className="text-gradient-cyan">intelligence engine</span>, not a risk register.
            </h2>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed">
              A five-stage pipeline that turns a strategic brief into a ministerial-grade risk register with VaR/CVaR tail metrics, Bow-Tie treatment paths, and a complete opportunity register — exportable as a PDF.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PIPELINE_STAGES.map((stage, i) => (
              <motion.div
                key={stage.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card rounded-lg p-6 lg:p-8 group"
              >
                <p className="text-xs font-mono text-brand-accent mb-4">{stage.step}</p>
                <stage.icon className="w-7 h-7 text-white mb-5 opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-base font-heading font-semibold mb-3">{stage.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{stage.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Scoring formula */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-10 md:mt-14 glass-card rounded-lg p-8 lg:p-10"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Scoring Formula</p>
            <p className="font-mono text-sm md:text-base text-white leading-relaxed break-words">
              Risk Score = (0.35 × Probability) + (0.35 × Impact) + (0.20 × Cascade Score) + (0.10 × CVaR<span className="text-brand-accent">₉₅</span>)
            </p>
            <p className="text-slate-500 text-xs mt-4">
              Output: CRITICAL / HIGH / MEDIUM / LOW banded risks with VaR/CVaR tail metrics.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-16 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-4">Module Architecture</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight mb-6">
              One platform. <span className="text-gradient-cyan">Every dimension</span> of risk.
            </h2>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed">
              RiskMind is not a single analysis tool — it is a platform of contextualised risk modules. Each module runs the full pipeline scoped to its domain. The Management View aggregates everything into one executive screen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES.map((mod, i) => (
              <motion.div
                key={mod.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="glass-card rounded-lg p-6 flex items-start gap-4"
              >
                <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <mod.icon size={16} className="text-brand-accent" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-1">{mod.name}</h4>
                  <p className="text-slate-500 text-xs leading-relaxed">{mod.domain}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 md:py-28 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-12 md:mb-16 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Architecture Principles</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight">
              Production-grade. Government-ready. <span className="text-gradient-cyan">Independently auditable.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card rounded-lg p-8 group"
              >
                <p.icon className="w-7 h-7 text-brand-accent mb-5 opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                <h3 className="text-base font-heading font-semibold mb-3">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2
            {...fadeUp}
            className="text-3xl md:text-5xl font-heading font-bold tracking-tight mb-6"
          >
            See RiskMind in action.
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-10"
          >
            Live platform. Ministerial-grade output. Book a walkthrough with the founders or visit the product site.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href={RISKMIND_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200 justify-center"
            >
              Visit riskmind.qa <ExternalLink size={16} />
            </a>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-white/5 transition-colors duration-200 justify-center"
            >
              Talk to Us <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
